class ApiService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
    this.token = this.getStoredToken();
    this.refreshPromise = null; // To prevent multiple refresh calls

    // Set up periodic token refresh check
    this.startTokenRefreshTimer();
  }

  // Token management
  getStoredToken() {
    return localStorage.getItem('auth_token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  removeToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
    this.refreshPromise = null; // Clear refresh promise
    this.stopTokenRefreshTimer(); // Stop refresh timer
  }

  // Get default headers
  getHeaders(includeAuth = true) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (includeAuth && this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  // Handle response parsing
  async handleResponse(response) {
    if (response.status === 204) {
      return null;
    }

    const contentType = response.headers.get('content-type');
    let responseData;

    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    return responseData;
  }

  // Refresh token if needed
  async refreshTokenIfNeeded() {
    // Prevent multiple simultaneous refresh calls
    if (this.refreshPromise) {
      return await this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  // Perform actual token refresh
  // With devise-jwt the server may issue a fresh token via the Authorization
  // response header on any authenticated request. We attempt a lightweight
  // call to a known endpoint. If the backend exposes a dedicated
  // /refresh_token route it will be used; otherwise we fall back to a
  // current-user lookup which still triggers devise-jwt token rotation.
  async performTokenRefresh() {
    // Try the dedicated refresh endpoint first
    let response;
    try {
      response = await fetch(`${this.baseUrl}/refresh_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
      });
    } catch {
      // Network error on refresh endpoint — fall through to fallback
      response = null;
    }

    // If the dedicated endpoint doesn't exist (404) or is unavailable,
    // fall back to a lightweight authenticated GET that triggers devise-jwt
    // token rotation via the Authorization response header.
    if (!response || response.status === 404 || response.status === 405) {
      const fallbackResponse = await fetch(
        `${this.baseUrl}/api/v1/credits/balance`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
        },
      );

      if (!fallbackResponse.ok) {
        throw new Error('Token refresh failed');
      }

      // Check for new token in response header (devise-jwt rotation)
      const authHeader = fallbackResponse.headers.get('Authorization');
      if (authHeader && authHeader.startsWith('Bearer ')) {
        this.setToken(authHeader.substring(7));
        return await this.handleResponse(fallbackResponse);
      }

      // No new token issued — current token is still valid
      return await this.handleResponse(fallbackResponse);
    }

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid or expired token');
      }
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    // Check for new token in response header first (devise-jwt standard)
    const authHeader = response.headers.get('Authorization');
    if (authHeader && authHeader.startsWith('Bearer ')) {
      this.setToken(authHeader.substring(7));
    }

    const data = await this.handleResponse(response);

    // Handle the API response format if body contains a token
    if (data.status && data.status.code === 200 && data.status.data) {
      if (data.status.data.token) {
        this.setToken(data.status.data.token);
      }

      if (data.status.data.user) {
        localStorage.setItem(
          'current_user',
          JSON.stringify(data.status.data.user),
        );
      }
    }

    return data;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      method: options.method || 'GET',
      headers: {
        ...this.getHeaders(options.includeAuth !== false),
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      // Check for JWT token in response headers
      const authHeader = response.headers.get('Authorization');
      if (authHeader && authHeader.startsWith('Bearer ')) {
        const newToken = authHeader.substring(7);
        this.setToken(newToken);
      }

      // Handle different response types
      const responseData = await this.handleResponse(response);

      if (!response.ok) {
        // Skip auto-logout for auth endpoints — a 401 on login/signup
        // means invalid credentials, not an expired session
        const isAuthEndpoint = endpoint === '/login' || endpoint === '/signup' || endpoint === '/password';

        // Handle authentication errors
        if (response.status === 401 && !isAuthEndpoint) {
          // If we have a token and haven't already retried, attempt refresh
          if (this.token && !config.skipTokenRefresh && !this.isTokenExpired()) {
            try {
              await this.refreshTokenIfNeeded();

              // Retry the original request with the refreshed token
              const retryResponse = await fetch(url, {
                ...config,
                skipTokenRefresh: true,
                headers: {
                  ...config.headers,
                  Authorization: `Bearer ${this.token}`,
                },
              });

              // Check for rotated token in retry response
              const retryAuth = retryResponse.headers.get('Authorization');
              if (retryAuth && retryAuth.startsWith('Bearer ')) {
                this.setToken(retryAuth.substring(7));
              }

              const retryData = await this.handleResponse(retryResponse);

              if (!retryResponse.ok) {
                const retryError = new Error(
                  retryData.message ||
                  `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
                );
                retryError.status = retryResponse.status;
                retryError.data = retryData;
                throw retryError;
              }

              return retryData;
            } catch {
              // Refresh or retry failed — session is truly expired
              this.removeToken();
              localStorage.removeItem('current_user');
              window.dispatchEvent(new CustomEvent('auth:logout'));
            }
          } else {
            // Token is expired or missing or retry already attempted — logout
            this.removeToken();
            localStorage.removeItem('current_user');
            window.dispatchEvent(new CustomEvent('auth:logout'));
          }
        }

        // Create error object with response data
        // Extract message from common API response formats:
        //   Devise:  { error: "Invalid Email or password." }
        //   Rails:   { status: { message: "..." } }
        //   Simple:  { message: "..." }
        //   Text:    plain string body
        let errorMessage;
        if (typeof responseData === 'string' && responseData.length > 0) {
          errorMessage = responseData;
        } else if (typeof responseData === 'object' && responseData !== null) {
          errorMessage =
            responseData.error ||
            responseData.message ||
            responseData.status?.message;
        }
        errorMessage = errorMessage || `HTTP ${response.status}: ${response.statusText}`;

        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = responseData;
        throw error;
      }

      return responseData;
    } catch (error) {
      // Network or parsing errors
      if (!error.status) {
        console.error('Network error:', error);
        throw new Error(
          'Network error. Please check your connection and try again.',
        );
      }
      throw error;
    }
  }

  // Convenience methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, { method: 'POST', body: data, ...options });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, { method: 'PUT', body: data, ...options });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, { method: 'PATCH', body: data, ...options });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { method: 'DELETE', ...options });
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.token && !this.isTokenExpired();
  }

  // Check if token is expired
  isTokenExpired() {
    if (!this.token) return true;

    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if token has 'exp' claim and if it's expired
      if (payload.exp && payload.exp < currentTime) {
        return true;
      }

      return false;
    } catch (error) {
      console.warn('Error checking token expiration:', error);
      return true; // Consider invalid tokens as expired
    }
  }

  // Get current user info from token (basic JWT decode)
  getCurrentUser() {
    if (!this.token || this.isTokenExpired()) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      return payload;
    } catch (error) {
      console.error('Error decoding token:', error);
      this.removeToken();
      return null;
    }
  }

  // Token refresh timer management
  startTokenRefreshTimer() {
    // Clear existing timer
    this.stopTokenRefreshTimer();

    // Check every minute if token needs refresh
    this.refreshTimer = setInterval(() => {
      if (this.token && this.shouldRefreshToken()) {
        this.refreshTokenIfNeeded().catch(() => {
          // Periodic refresh failed silently — will retry next interval
        });
      }
    }, 60 * 1000); // 1 minute
  }

  stopTokenRefreshTimer() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Check if token should be refreshed (e.g., expires in next 15 minutes)
  shouldRefreshToken() {
    if (!this.token) return false;

    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const refreshThreshold = 15 * 60; // 15 minutes before expiry

      // Refresh if token expires within the threshold
      if (payload.exp && payload.exp - currentTime < refreshThreshold) {
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  // Get seconds until token expires (for debugging / proactive refresh)
  getTokenExpiresIn() {
    if (!this.token) return 0;

    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp ? payload.exp - currentTime : Infinity;
    } catch {
      return 0;
    }
  }

  // Logout and clear all data
  async logout() {
    try {
      // Call backend logout endpoint
      if (this.token) {
        await this.delete('/logout');
      }
    } catch (error) {
      console.warn(
        'Backend logout failed, continuing with local logout:',
        error,
      );
    } finally {
      // Always clear local data regardless of backend response
      this.removeToken();
      localStorage.removeItem('current_user');
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();
